import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckIcon from '@mui/icons-material/Check';
import { Menu } from "@mui/material";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { TargetPlatform } from "../../../../shared/types/TargetPlatform";
import { targetPlatformOptions } from "../TargetPlatformOptions";
import { ContextMenuItem } from "./ContextMenuItem";

type TargetPlatformContextMenuItemProps = {
    value: TargetPlatform;
    onValueChange: (value: TargetPlatform) => void | Promise<void>;
    closeContextMenu: () => void;
    disabled?: boolean;
};

export function TargetPlatformContextMenuItem({
    value,
    onValueChange,
    closeContextMenu,
    disabled,
}: TargetPlatformContextMenuItemProps) {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelScheduledClose = (): void => {
        if (closeTimeout.current !== null) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    };

    const closeMenuList = (): void => {
        cancelScheduledClose();
        setAnchorElement(null);
    };

    const scheduleMenuListClose = (): void => {
        cancelScheduledClose();
        closeTimeout.current = setTimeout(() => {
            setAnchorElement(null);
            closeTimeout.current = null;
        }, 150);
    };

    useEffect(() => () => {
        if (closeTimeout.current !== null) {
            clearTimeout(closeTimeout.current);
        }
    }, []);

    const handleOpen = (event: MouseEvent<HTMLElement>): void => {
        event.stopPropagation();

        if (!disabled) {
            cancelScheduledClose();
            setAnchorElement(event.currentTarget);
        }
    };

    const handleValueChange = async (targetPlatform: TargetPlatform): Promise<void> => {
        closeMenuList();
        closeContextMenu();

        if (targetPlatform !== value) {
            await onValueChange(targetPlatform);
        }
    };

    return (
        <>
            <ContextMenuItem
                aria-haspopup="menu"
                aria-expanded={anchorElement !== null ? "true" : undefined}
                onClick={handleOpen}
                onMouseEnter={handleOpen}
                onMouseLeave={scheduleMenuListClose}
                disabled={disabled}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                Target platform
                <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
            </ContextMenuItem>
            <Menu
                anchorEl={anchorElement}
                open={anchorElement !== null}
                onClose={closeMenuList}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                transitionDuration={0}
                MenuListProps={{
                    dense: true,
                    onMouseEnter: cancelScheduledClose,
                    onMouseLeave: closeMenuList,
                    sx: { py: 0.25 },
                }}
                sx={{
                    pointerEvents: 'none',
                    '& .MuiMenu-paper': {
                        backgroundColor: 'rgba(240,240,240)',
                        pointerEvents: 'auto',
                    },
                }}
            >
                {targetPlatformOptions.map(option => (
                    <ContextMenuItem
                        key={option.value}
                        role="menuitemradio"
                        aria-checked={option.value === value}
                        onClick={() => void handleValueChange(option.value)}
                    >
                        <CheckIcon
                            aria-hidden="true"
                            sx={{
                                width: 18,
                                mr: 0.5,
                                flexShrink: 0,
                                visibility: option.value === value ? 'visible' : 'hidden',
                            }}
                        />
                        {option.label}
                    </ContextMenuItem>
                ))}
            </Menu>
        </>
    );
}
